import React, { useState, useEffect } from 'react';
import { useUser } from '../UserContext';
import PrizeNavigation from '../components/PrizeNavigation';
import Button from '../components/Button';
import EditForm from '../components/EditForm';
import Modal from '../components/Modal';
import KeepersTable from '../components/KeepersTable';
import styles from './Players.module.css';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import API_BASE_URL from '../utils/apiConfig';

const Players = () => {
  const { user } = useUser();
  const [showForm, setShowForm] = useState(false);
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null); // Track the player being edited
  const [isEditing, setIsEditing] = useState(false);
  const [initialValues, setInitialValues] = useState({});

  useEffect(() => { // Get players from the API
    const fetchPlayers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/players`, {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });
        const data = await response.json();
        setPlayers(data);
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    };

    fetchPlayers();
  }, [user.token]);

  const playerColumns = [
    {
      name: 'actions',
      label: 'Actions',
      render: (player) => {
        return (
          <IconButton
            aria-label="Edit Player"
            size="small"
            className={styles.editIcon}
            onClick={() => handleEditPlayer(player)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        );
      },
    },  
    { name: 'position', label: 'Position' },
    { name: 'name', label: 'Player Name' },
    { name: 'category', label: 'Category' },
    { name: 'desiredCost', label: 'Price / Round' }
  ];

  const playerFields = [
    { name: 'position', label: 'Position', type: 'select', options: ['WR', 'QB', 'RB', 'TE', 'K', 'DEF'], required: true },
    { name: 'name', label: 'Name', type: 'text', placeholder: 'Enter Player Name', required: true },
    { name: 'category', label: 'Category', type: 'select', options: ['Stud', 'Bust', 'Sleeper', 'Safe'], required: true },
    { name: 'desiredCost', label: 'Price / Round', type: 'text', placeholder: 'Target Player Cost', required: false }
  ];

  const handleEditPlayer = (player) => {
    const newInitialValues = {
      position: player.position,
      name: player.name,
      category: player.category,
      desiredCost: player.desiredCost,
    };
  
    // 🧠 Set form values *before* showing the form
    setInitialValues(newInitialValues);
    setSelectedPlayer(player);
    setIsEditing(true);
  
    // ✨ Add a short delay to allow dropdown options to populate
    setTimeout(() => {
      setShowForm(true);
    }, 0);
  };

    // Handle form submission for adding or editing a player
    const handleFormSubmit = async (formData) => {
      try {
        const playerPayload = {
          position: formData.position, // Use formData instead of player
          name: formData.name,
          category: formData.category,
          desiredCost: formData.desiredCost,
        };
    
        let response;
        let playerData;
    
        if (selectedPlayer) {
          // Update existing player
          response = await fetch(`${API_BASE_URL}/api/players/${selectedPlayer._id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${user.token}`,
            },
            body: JSON.stringify(playerPayload),
          });
    
          if (!response.ok) {
            throw new Error('Failed to update player');
          }
    
          playerData = await response.json();
    
          setPlayers((prevPlayers) =>
            prevPlayers.map((player) =>
              player._id === playerData._id ? playerData : player
            )
          );
        } else {
          // Add new player
          response = await fetch(`${API_BASE_URL}/api/players`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${user.token}`,
            },
            body: JSON.stringify(playerPayload),
          });
    
          if (!response.ok) {
            throw new Error('Failed to add player');
          }
    
          playerData = await response.json();
          setPlayers((prevPlayers) => [...prevPlayers, playerData]);
        }
    
        // Reset form state
        setShowForm(false);
        setSelectedPlayer(null);
      } catch (error) {
        console.error('Error saving player:', error);
        alert('Oops. There was an error saving the player. Please try again.');
      }
    };

  const closeModal = () => {
    setShowForm(false);
    setSelectedPlayer(null);
    setInitialValues({}); // Force EditForm to reset
  };

  const handleDeletePlayer = async () => { // Delete the selected player
    if (!selectedPlayer) {
      console.error('No player selected for deletion.');
      return;
    }
  
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this player record for "${selectedPlayer.name}"?`
    );
    if (!confirmDelete) return;
  
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/players/${selectedPlayer._id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${user.token}`,
          },
        }
      );
  
      if (!response.ok) {
        throw new Error('Error deleting player');
      }
  
      // Remove the deleted player from the state
      setPlayers((prevPlayers) =>
        prevPlayers.filter((player) => player._id !== selectedPlayer._id)
      );
  
      // Close modal and reset state
      setShowForm(false);
      setSelectedPlayer(null);
    } catch (error) {
      console.error('Error deleting player:', error);
      alert('Oops, there was a problem deleting the player. Please try again.');
    }
  };

  return (
    <>
      <div>
        <PrizeNavigation navigationButtonArea="/2813821-1@2x.png" />
      </div>
      <div className={styles.playersHeader}>
        <h3 className={styles.playersTitle}>Players</h3>
        <p>Consider this your one stop shop for players you want target for your draft.</p>
        <p>Use this space to track players you want to target in your draft by adding a player and assiging a desired cost and grouping them into buckets of players</p>
        <div className={styles.addPlayer}>
          <Button
            label={"Add player"}
            onClick={() => setShowForm(true)} // Show the form when button is clicked
          />
        </div>
      </div>
      <div>
        <KeepersTable data={players} columns={playerColumns} />
      </div>

      {/* Render Modal with the form when showForm is true */}
      {showForm && (
        <Modal onClose={closeModal}>
          <EditForm
            context="players"
            selectedEntry={selectedPlayer}
            // currentLeagueId={leagueId}
            fields={playerFields}
            title={selectedPlayer ? "Edit Player" : "Add Player"}
            onSubmit={handleFormSubmit} // Handle form data submission
            initialValues={initialValues || {}}
            isEditing={isEditing}
            selectedPlayer={selectedPlayer}
          />

          {/* Show the Delete button only when editing an existing player */}
            {selectedPlayer && (
              <div
                className={styles.deletePlayer}
                onClick={handleDeletePlayer}
              >
                Delete
              </div>
            )}
        </Modal>
      )}
    </>
  );
};

export default Players;